'use client';

import { useState } from 'react';
import { 
  ArrowRight, 
  Search,
  ChefHat,
  Clock,
  Users,
  Sparkles,
  BookOpen,
  Star,
  Heart,
  TrendingUp,
  Filter,
  Wand2,
  Utensils,
  Soup,
  Flame,
  Fish,
  Wheat,
  Lightbulb,
  Wine,
  Globe,
  Zap,
  X,
  ChevronRight
} from 'lucide-react';
import { Button } from '@myapp/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@myapp/ui/components/card';
import { Badge } from '@myapp/ui/components/badge';
import { Input } from '@myapp/ui/components/input';
import { Textarea } from '@myapp/ui/components/textarea';
import { motion, AnimatePresence } from 'framer-motion';

// AI 레시피 생성 기능
const generateRecipe = async (ingredients: string, preferences: string) => {
  try {
    // API 경로를 절대 경로로 수정
    const baseUrl = window.location.origin;
    const response = await fetch(`${baseUrl}/api/generate-recipe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ingredients,
        preferences
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.recipe) {
      return {
        title: data.recipe.name,
        description: data.recipe.description,
        cookingTime: data.recipe.cookTime || data.recipe.cookingTime,
        difficulty: data.recipe.difficulty,
        servings: data.recipe.servings,
        ingredients: data.recipe.ingredients,
        instructions: data.recipe.instructions,
        tips: data.recipe.tips,
        nutrition: data.recipe.nutrition,
        variations: data.recipe.variations || [],
        pairings: data.recipe.pairings || [],
        enhanced: data.enhanced || false,
        searchUsed: data.searchUsed || false
      };
    }
    
    throw new Error('Failed to generate recipe');
  } catch (error) {
    console.error('Recipe generation error:', error);
    // 폴백 레시피 반환
    return {
      title: `${ingredients}를 활용한 특별 레시피`,
      description: `신선한 강원도 ${ingredients}로 만드는 건강하고 맛있는 요리입니다.`,
      cookingTime: '35분',
      difficulty: '보통',
      servings: '2-3인분',
      ingredients: [
        `${ingredients} 500g`,
        '양파 1개',
        '마늘 3쪽',
        '고춧가루 1큰술',
        '간장 2큰술',
        '참기름 1큰술',
        '소금, 후추 약간'
      ],
      instructions: [
        `${ingredients}를 깨끗이 손질하고 적당한 크기로 자릅니다.`,
        '양파와 마늘을 다져줍니다.',
        '팬에 기름을 두르고 마늘을 볶아 향을 냅니다.',
        `손질한 ${ingredients}를 넣고 볶습니다.`,
        '양념을 넣고 골고루 섞어 완성합니다.'
      ],
      tips: [
        `${ingredients}는 너무 오래 볶지 말고 아삭한 식감을 살려주세요.`,
        '고춧가루는 취향에 따라 조절하세요.',
        '마지막에 참기름을 넣으면 더욱 고소합니다.'
      ],
      nutrition: {
        calories: '180kcal',
        protein: '8g',
        carbs: '25g',
        fat: '6g'
      },
      variations: [],
      pairings: [],
      enhanced: false,
      searchUsed: false
    };
  }
};

// 카테고리별 아이콘 매핑
const categoryIcons = {
  '전통요리': ChefHat,
  '면요리': Wheat,
  '구이요리': Flame,
  '생선요리': Fish,
  '찌개/국': Soup
};

// 미리 정의된 레시피들
const featuredRecipes = [
  {
    id: 1,
    title: '감자전 황금레시피',
    description: '바삭하면서도 촉촉한 강원도 감자전 만들기',
    image: '🥔',
    category: '전통요리',
    categoryIcon: ChefHat,
    time: '30분',
    difficulty: '쉬움',
    rating: 4.8,
    likes: 124,
    servings: '2-3인분',
    featured: true,
    tags: ['간식', '전통', '쉬운요리'],
    ingredients: [
      '감자 중간 크기 3개 (약 300g)',
      '양파 1/4개',
      '대파 1대',
      '밀가루 3큰술',
      '달걀 1개',
      '소금 1/2작은술',
      '식용유 적당량'
    ],
    instructions: [
      '감자를 깨끗이 씻어 껍질을 벗기고 강판에 갈아줍니다.',
      '갈은 감자는 면포에 싸서 물기를 꼼꼼히 짜냅니다.',
      '양파와 대파를 잘게 다져줍니다.',
      '감자에 양파, 대파, 밀가루, 달걀, 소금을 넣고 골고루 섞어줍니다.',
      '팬에 식용유를 두르고 중불에서 전을 지져줍니다.',
      '앞뒤로 노릇하게 구워 완성합니다.'
    ],
    tips: [
      '감자 물기를 충분히 제거해야 바삭한 식감이 납니다.',
      '반죽은 너무 묽지 않게 농도를 맞춰주세요.',
      '중불에서 천천히 구워야 속까지 익습니다.'
    ],
    nutrition: {
      calories: '220kcal',
      protein: '6g',
      carbs: '35g',
      fat: '8g'
    }
  },
  {
    id: 2,
    title: '쟁반비빔막국수',
    description: 'AI가 분석한 최적의 양념 비율로 만드는 막국수',
    image: '🍜',
    category: '면요리',
    categoryIcon: Wheat,
    time: '35분',
    difficulty: '보통',
    rating: 4.9,
    likes: 89,
    servings: '2인분',
    featured: true,
    tags: ['시원한', '여름', '매콤한'],
    ingredients: [
      '메밀면 200g',
      '오이 1개',
      '당근 1/2개',
      '양파 1/2개',
      '삶은 달걀 2개',
      '고춧가루 2큰술',
      '고추장 1큰술',
      '식초 3큰술',
      '설탕 1큰술',
      '참기름 1큰술',
      '마늘 1큰술',
      '소금 약간'
    ],
    instructions: [
      '메밀면을 끓는 물에 삶아 찬물에 헹궈둡니다.',
      '오이, 당근, 양파를 채썰어 준비합니다.',
      '양념장 재료를 모두 섞어 만들어둡니다.',
      '큰 쟁반에 면과 채소를 담고 양념장을 올립니다.',
      '삶은 달걀을 반으로 잘라 올리고 골고루 비벼 완성합니다.'
    ],
    tips: [
      '면은 찬물에 여러 번 헹궈 전분을 제거하세요.',
      '채소는 얇게 채썰수록 맛이 잘 배어듭니다.',
      '양념장은 미리 만들어 두면 맛이 더 좋아집니다.'
    ],
    nutrition: {
      calories: '380kcal',
      protein: '12g',
      carbs: '65g',
      fat: '8g'
    }
  },
  {
    id: 3,
    title: '춘천식 닭갈비',
    description: '정통 춘천 닭갈비의 비밀 레시피',
    image: '🍗',
    category: '구이요리',
    categoryIcon: Flame,
    time: '40분',
    difficulty: '보통',
    rating: 4.7,
    likes: 156,
    servings: '3-4인분',
    featured: true,
    tags: ['매콤한', '메인요리', '파티'],
    ingredients: [
      '닭다리살 600g',
      '양배추 1/4통',
      '양파 1개',
      '떡 200g',
      '고춧가루 3큰술',
      '고추장 2큰술',
      '간장 2큰술',
      '설탕 1큰술',
      '마늘 1큰술',
      '생강 1작은술',
      '참기름 1큰술',
      '대파 2대'
    ],
    instructions: [
      '닭다리살을 한입 크기로 잘라 준비합니다.',
      '양념재료를 모두 섞어 닭고기를 30분간 재워둡니다.',
      '양배추와 양파는 먹기 좋게 썰어둡니다.',
      '팬에 재운 닭고기를 넣고 볶아줍니다.',
      '닭고기가 반쯤 익으면 채소와 떡을 넣고 함께 볶습니다.',
      '모든 재료가 잘 익으면 대파를 넣고 마무리합니다.'
    ],
    tips: [
      '닭고기는 충분히 재워야 맛이 깊어집니다.',
      '센 불에서 빠르게 볶아야 채소가 아삭해집니다.',
      '떡은 미리 물에 불려두면 더 부드럽습니다.'
    ],
    nutrition: {
      calories: '420kcal',
      protein: '28g',
      carbs: '32g',
      fat: '18g'
    }
  },
  {
    id: 4,
    title: '황태구이 맛집스타일',
    description: '황태의 깊은 맛을 살린 구이 요리',
    image: '🐟',
    category: '생선요리',
    categoryIcon: Fish,
    time: '25분',
    difficulty: '쉬움',
    rating: 4.6,
    likes: 67,
    servings: '2인분',
    featured: false,
    tags: ['단백질', '건강한', '술안주'],
    ingredients: [
      '황태 2마리 (중간 크기)',
      '마늘 3쪽',
      '생강 1조각',
      '대파 2대',
      '참기름 2큰술',
      '간장 1큰술',
      '맛술 1큰술',
      '후추 약간'
    ],
    instructions: [
      '황태를 천천히 해동시켜 물기를 제거합니다.',
      '마늘, 생강은 다지고 대파는 어슷하게 썰어둡니다.',
      '팬에 참기름을 두르고 마늘, 생강을 볶아 향을 냅니다.',
      '황태를 넣고 양면을 골고루 구워줍니다.',
      '간장, 맛술, 후추로 간을 맞춘 뒤 대파를 넣어 완성합니다.'
    ],
    tips: [
      '황태는 완전히 해동된 상태에서 사용하세요.',
      '너무 오래 구우면 바시고 딱딱해지니 주의하세요.',
      '대파는 마지막에 넣어 색과 향을 살려주세요.'
    ],
    nutrition: {
      calories: '150kcal',
      protein: '22g',
      carbs: '5g',
      fat: '6g'
    }
  },
  {
    id: 5,
    title: '곰취나물 산채비빔밥',
    description: '영양 만점 산나물로 만드는 건강 비빔밥',
    image: '🥗',
    category: '전통요리',
    categoryIcon: ChefHat,
    time: '50분',
    difficulty: '어려움',
    rating: 4.5,
    likes: 234,
    servings: '1인분',
    featured: false,
    tags: ['건강한', '채식', '영양'],
    ingredients: [
      '곰취 100g',
      '도라지 50g',
      '고사리 30g',
      '시금치 50g',
      '백반 1공기',
      '참기름 2큰술',
      '고춧가루 1큰술',
      '간장 2큰술',
      '설탕 1작은술',
      '마늘 2쪽',
      '소금 약간'
    ],
    instructions: [
      '곰취, 도라지, 고사리를 각각 따로 끓는 물에 삶아줍니다.',
      '삶은 나물들을 찬물에 헹궈 식혀줍니다.',
      '각 나물들을 양념장으로 무쳐줍니다.',
      '시금치는 생것 그대로 무쳐줍니다.',
      '계란 프라이를 만들어 채썰어 준비합니다.',
      '밥 위에 각종 나물과 계란을 예쁘게 담고 고추장을 올립니다.'
    ],
    tips: [
      '나물들은 각각의 특성에 맞게 따로 삶아주세요.',
      '양념은 간이 배기 전에 미리 만들어 두세요.',
      '마지막에 참기름 몇 방울을 떨어뜨리면 더욱 고소합니다.'
    ],
    nutrition: {
      calories: '450kcal',
      protein: '15g',
      carbs: '65g',
      fat: '12g'
    }
  },
  {
    id: 6,
    title: '평창 감자옹심이',
    description: '쫄깃한 식감의 전통 감자옹심이',
    image: '🥟',
    category: '전통요리',
    categoryIcon: ChefHat,
    time: '60분',
    difficulty: '어려움',
    rating: 4.8,
    likes: 91,
    servings: '4인분',
    featured: false,
    tags: ['전통', '따뜻한', '겨울'],
    ingredients: [
      '감자 대 3개',
      '감자전분 1컵',
      '닭가슴살 100g',
      '양파 1/2개',
      '당근 1/3개',
      '대파 2대',
      '가시동태 내장 1개',
      '국간장 2큰술',
      '소금 1작은술',
      '후추 약간'
    ],
    instructions: [
      '감자를 강판에 갈아 물기를 제거합니다.',
      '간 감자에 감자전분과 소금을 넣고 반죽합니다.',
      '반죽을 작은 덩어리로 떼어 옵심이를 만들어줍니다.',
      '닭가슴살, 양파, 당근을 작게 다져줍니다.',
      '끓는 물에 가시동태 내장을 넣고 육수를 냅니다.',
      '육수에 채소와 옵심이를 넣고 끓인 뒤 간을 맞춰 완성합니다.'
    ],
    tips: [
      '감자 반죽은 끈적이지 않게 농도를 맞춰주세요.',
      '옵심이의 크기를 똑같이 만들면 골고루 익습니다.',
      '육수는 차가운 상태에서 옵심이를 넣어야 모양이 깨지지 않습니다.'
    ],
    nutrition: {
      calories: '280kcal',
      protein: '12g',
      carbs: '52g',
      fat: '4g'
    }
  }
];

const categories = [
  { id: 'all', name: '전체', icon: Utensils, count: featuredRecipes.length },
  { id: 'traditional', name: '전통요리', icon: ChefHat, count: 4 },
  { id: 'noodles', name: '면요리', icon: Wheat, count: 1 },
  { id: 'grilled', name: '구이요리', icon: Flame, count: 2 },
  { id: 'seafood', name: '생선요리', icon: Fish, count: 1 }
];

// 강원도 인기 식재료
const popularIngredients = [
  { name: '감자', emoji: '🥔' },
  { name: '황태', emoji: '🐟' },
  { name: '메밀', emoji: '🌾' },
  { name: '옥수수', emoji: '🌽' },
  { name: '산나물', emoji: '🌿' },
  { name: '한우', emoji: '🥩' },
  { name: '오징어', emoji: '🦑' },
  { name: '닭갈비', emoji: '🍗' }
];

export default function RecipesPage() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [aiIngredients, setAiIngredients] = useState('');
  const [aiPreferences, setAiPreferences] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedRecipe, setGeneratedRecipe] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [isRecipeModalOpen, setIsRecipeModalOpen] = useState(false);

  const filteredRecipes = featuredRecipes.filter(recipe => {
    const matchesSearch = recipe.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         recipe.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const handleGenerateRecipe = async () => {
    if (!aiIngredients.trim()) return;
    
    setIsGenerating(true);
    try {
      const recipe = await generateRecipe(aiIngredients, aiPreferences);
      setGeneratedRecipe(recipe);
    } catch (error) {
      console.error('Recipe generation failed:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRecipeView = (recipe) => {
    setSelectedRecipe(recipe);
    setIsRecipeModalOpen(true);
  };

  const closeRecipeModal = () => {
    setIsRecipeModalOpen(false);
    setSelectedRecipe(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-yellow-50">
      {/* Header */}
      <section className="bg-gradient-to-r from-orange-500 to-red-500 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4 bg-white/20 border-white/30 text-white">
              <ChefHat className="w-3 h-3 mr-1" />
              AI 맞춤 레시피
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              AI가 추천하는 강원도 레시피
            </h1>
            <p className="text-xl text-orange-50 mb-8">
              강원도 특산물을 활용한 맞춤형 레시피를 AI가 제안해드립니다
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-white text-orange-600 hover:bg-gray-50 px-8 py-3 text-lg rounded-xl"
                onClick={() => setShowAIGenerator(!showAIGenerator)}
              >
                <Wand2 className="w-5 h-5 mr-2" />
                AI 레시피 생성하기
              </Button>
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="text"
                  placeholder="레시피 검색..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white/90 border-white/20 rounded-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Recipe Generator */}
      {showAIGenerator && (
        <section className="bg-white border-b py-8">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              {/* 인기 식재료 퀵메뉴 */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  <span className="text-sm font-semibold text-gray-700">인기 식재료 퀵선택</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {popularIngredients.map((ingredient) => (
                    <Button
                      key={ingredient.name}
                      variant="outline"
                      size="sm"
                      onClick={() => setAiIngredients(ingredient.name)}
                      className="hover:bg-orange-50 hover:border-orange-300"
                    >
                      <span className="mr-1">{ingredient.emoji}</span>
                      {ingredient.name}
                    </Button>
                  ))}
                </div>
              </div>
              <Card className="border-orange-200 shadow-lg">
                <CardHeader className="bg-gradient-to-r from-orange-50 to-yellow-50">
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-orange-500" />
                    AI 맞춤 레시피 생성기
                  </CardTitle>
                  <p className="text-gray-600">
                    가지고 있는 재료와 선호도를 입력하면 AI가 맞춤 레시피를 생성해드립니다
                  </p>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        주재료 (필수)
                      </label>
                      <Input
                        type="text"
                        placeholder="예: 감자, 옥수수, 닭고기"
                        value={aiIngredients}
                        onChange={(e) => setAiIngredients(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        선호도 및 요청사항 (선택)
                      </label>
                      <Textarea
                        placeholder="예: 매콤하게, 간단하게, 아이들용"
                        value={aiPreferences}
                        onChange={(e) => setAiPreferences(e.target.value)}
                        className="w-full h-20 resize-none"
                      />
                    </div>
                  </div>
                  <div className="mt-6 text-center">
                    <Button
                      onClick={handleGenerateRecipe}
                      disabled={!aiIngredients.trim() || isGenerating}
                      className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-8 py-3"
                    >
                      {isGenerating ? (
                        <>
                          <div className="animate-spin w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full" />
                          AI가 레시피를 생성 중...
                        </>
                      ) : (
                        <>
                          <Wand2 className="w-4 h-4 mr-2" />
                          AI 레시피 생성하기
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Generated Recipe Display */}
              {generatedRecipe && (
                <Card className="mt-6 border-green-200 shadow-lg">
                  <CardHeader className="bg-gradient-to-r from-green-50 to-blue-50">
                    <CardTitle className="flex items-center gap-2">
                      <ChefHat className="w-5 h-5 text-green-500" />
                      {generatedRecipe.title}
                    </CardTitle>
                    <p className="text-gray-600">{generatedRecipe.description}</p>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                      <div className="text-center">
                        <Clock className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                        <div className="font-semibold">{generatedRecipe.cookingTime}</div>
                        <div className="text-sm text-gray-600">조리시간</div>
                      </div>
                      <div className="text-center">
                        <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                        <div className="font-semibold">{generatedRecipe.servings}</div>
                        <div className="text-sm text-gray-600">분량</div>
                      </div>
                      <div className="text-center">
                        <BookOpen className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                        <div className="font-semibold">{generatedRecipe.difficulty}</div>
                        <div className="text-sm text-gray-600">난이도</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      <div>
                        <h4 className="font-semibold text-lg mb-3 text-gray-800">재료</h4>
                        <ul className="space-y-2">
                          {generatedRecipe.ingredients.map((ingredient, index) => (
                            <li key={index} className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-orange-400 rounded-full" />
                              <span className="text-gray-700">{ingredient}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-lg mb-3 text-gray-800">영양정보</h4>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>칼로리: {generatedRecipe.nutrition.calories}</div>
                            <div>단백질: {generatedRecipe.nutrition.protein}</div>
                            <div>탄수화물: {generatedRecipe.nutrition.carbs}</div>
                            <div>지방: {generatedRecipe.nutrition.fat}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-8">
                      <h4 className="font-semibold text-lg mb-3 text-gray-800">조리방법</h4>
                      <ol className="space-y-3">
                        {generatedRecipe.instructions.map((step, index) => (
                          <li key={index} className="flex gap-4">
                            <div className="flex-shrink-0 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </div>
                            <span className="text-gray-700 pt-0.5">{step}</span>
                          </li>
                        ))}
                      </ol>
                    </div>

                    <div className="mt-8">
                      <h4 className="font-semibold text-lg mb-3 text-gray-800">요리 팁</h4>
                      <ul className="space-y-2">
                        {generatedRecipe.tips.map((tip, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <Sparkles className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {generatedRecipe.variations && generatedRecipe.variations.length > 0 && (
                      <div className="mt-8">
                        <h4 className="font-semibold text-lg mb-3 text-gray-800 flex items-center gap-2">
                          <Lightbulb className="w-5 h-5 text-purple-500" />
                          레시피 변형 아이디어
                        </h4>
                        <ul className="space-y-2">
                          {generatedRecipe.variations.map((variation, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <div className="w-2 h-2 bg-purple-400 rounded-full mt-1.5 flex-shrink-0" />
                              <span className="text-gray-700">{variation}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {generatedRecipe.pairings && generatedRecipe.pairings.length > 0 && (
                      <div className="mt-8">
                        <h4 className="font-semibold text-lg mb-3 text-gray-800 flex items-center gap-2">
                          <Wine className="w-5 h-5 text-red-500" />
                          추천 페어링
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {generatedRecipe.pairings.map((pairing, index) => (
                            <Badge key={index} variant="outline" className="bg-red-50 border-red-200 text-red-700">
                              {pairing}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {generatedRecipe.enhanced && (
                      <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
                        <div className="flex items-center gap-2 text-sm text-purple-700">
                          <Globe className="w-4 h-4" />
                          <span className="font-medium">
                            AI가 실시간 검색으로 최신 레시피 트렌드를 반영했습니다
                          </span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Categories */}
      <section className="bg-white border-b py-4 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-4">
            <Filter className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">카테고리:</span>
            <div className="flex gap-2 overflow-x-auto">
              {categories.map((category) => (
                <Button
                  key={category.id}
                  variant={selectedCategory === category.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category.id)}
                  className="text-xs whitespace-nowrap flex items-center gap-1"
                >
                  {category.icon && <category.icon className="w-3 h-3" />}
                  {category.name} ({category.count})
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Recipes */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              <TrendingUp className="w-8 h-8 text-orange-500 inline mr-2" />
              인기 레시피
            </h2>
            <p className="text-gray-600">
              강원도 특산물을 활용한 검증된 인기 레시피들을 만나보세요
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredRecipes.map((recipe) => (
              <Card key={recipe.id} className="group hover:shadow-xl transition-all duration-300 overflow-hidden">
                <div className="relative">
                  <div className="aspect-video bg-gradient-to-br from-orange-100 to-yellow-100 flex items-center justify-center text-6xl relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-orange-200/20 to-yellow-200/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <span className="relative z-10 group-hover:scale-110 transition-transform duration-300">
                      {recipe.image}
                    </span>
                  </div>
                  
                  {recipe.featured && (
                    <div className="absolute top-3 left-3">
                      <Badge className="bg-orange-500 text-white text-xs">
                        인기
                      </Badge>
                    </div>
                  )}
                  
                  <div className="absolute top-3 right-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="bg-white/80 hover:bg-white text-gray-600 rounded-full p-2"
                    >
                      <Heart className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline" className="text-xs bg-orange-50 border-orange-200 text-orange-700 flex items-center gap-1">
                      {recipe.categoryIcon && <recipe.categoryIcon className="w-3 h-3" />}
                      {recipe.category}
                    </Badge>
                    <div className="flex items-center text-sm text-gray-600">
                      <Star className="w-3 h-3 text-yellow-500 fill-current mr-1" />
                      {recipe.rating}
                    </div>
                  </div>

                  <h3 className="font-bold text-lg text-gray-900 group-hover:text-orange-600 transition-colors mb-2">
                    {recipe.title}
                  </h3>
                  
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {recipe.description}
                  </p>

                  <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {recipe.time}
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {recipe.servings}
                    </div>
                    <div className="flex items-center gap-1">
                      <BookOpen className="w-3 h-3" />
                      {recipe.difficulty}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1 mb-4">
                    {recipe.tags.map((tag, index) => (
                      <Badge key={index} variant="outline" className="text-xs px-2 py-0.5 bg-gray-50 border-gray-200 text-gray-600">
                        {tag}
                      </Badge>
                    ))}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-500">
                      <Heart className="w-3 h-3 mr-1" />
                      {recipe.likes}
                    </div>
                    <Button 
                      size="sm" 
                      className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white"
                      onClick={() => handleRecipeView(recipe)}
                    >
                      레시피 보기
                      <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredRecipes.length === 0 && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                검색 결과가 없습니다
              </h3>
              <p className="text-gray-600">
                다른 검색어를 시도해보세요
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-orange-500 to-red-500 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            나만의 레시피를 만들어보세요!
          </h2>
          <p className="text-orange-50 mb-8 max-w-2xl mx-auto">
            AI가 여러분의 재료와 취향에 맞는 특별한 레시피를 생성해드립니다
          </p>
          <Button
            size="lg"
            className="bg-white text-orange-600 hover:bg-gray-50 px-8 py-3 text-lg rounded-xl"
            onClick={() => setShowAIGenerator(true)}
          >
            <Wand2 className="w-5 h-5 mr-2" />
            AI 레시피 생성하기
          </Button>
        </div>
      </section>

      {/* Recipe Detail Modal */}
      <AnimatePresence>
        {isRecipeModalOpen && selectedRecipe && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeRecipeModal}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            >
              {/* Modal Content */}
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
              >
                {/* Modal Header */}
                <div className="relative bg-gradient-to-r from-orange-500 to-red-500 text-white p-6">
                  <button
                    onClick={closeRecipeModal}
                    className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  
                  <div className="flex items-start gap-4">
                    <div className="text-6xl">{selectedRecipe.image}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-white/20 border-white/30 text-white text-xs">
                          {selectedRecipe.categoryIcon && <selectedRecipe.categoryIcon className="w-3 h-3 mr-1" />}
                          {selectedRecipe.category}
                        </Badge>
                        <div className="flex items-center text-sm">
                          <Star className="w-4 h-4 text-yellow-300 fill-current mr-1" />
                          {selectedRecipe.rating}
                        </div>
                      </div>
                      <h2 className="text-2xl md:text-3xl font-bold mb-2">{selectedRecipe.title}</h2>
                      <p className="text-orange-50 text-sm md:text-base">{selectedRecipe.description}</p>
                      
                      <div className="flex flex-wrap gap-2 mt-3">
                        {selectedRecipe.tags.map((tag, index) => (
                          <Badge key={index} variant="outline" className="bg-white/10 border-white/30 text-white text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Body */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                  {/* Recipe Info */}
                  <div className="grid grid-cols-3 gap-4 mb-8">
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                      <Clock className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                      <div className="font-semibold text-gray-900">{selectedRecipe.time}</div>
                      <div className="text-sm text-gray-600">조리시간</div>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                      <div className="font-semibold text-gray-900">{selectedRecipe.servings}</div>
                      <div className="text-sm text-gray-600">분량</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <BookOpen className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                      <div className="font-semibold text-gray-900">{selectedRecipe.difficulty}</div>
                      <div className="text-sm text-gray-600">난이도</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Ingredients */}
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <ChefHat className="w-5 h-5 text-orange-500" />
                        재료
                      </h3>
                      <div className="space-y-2">
                        {selectedRecipe.ingredients?.map((ingredient, index) => (
                          <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <div className="w-2 h-2 bg-orange-400 rounded-full flex-shrink-0" />
                            <span className="text-gray-700">{ingredient}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Nutrition Info */}
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <Heart className="w-5 h-5 text-red-500" />
                        영양정보
                      </h3>
                      <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-lg p-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-green-600">{selectedRecipe.nutrition?.calories}</div>
                            <div className="text-sm text-gray-600">칼로리</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">{selectedRecipe.nutrition?.protein}</div>
                            <div className="text-sm text-gray-600">단백질</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-yellow-600">{selectedRecipe.nutrition?.carbs}</div>
                            <div className="text-sm text-gray-600">탄수화물</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-purple-600">{selectedRecipe.nutrition?.fat}</div>
                            <div className="text-sm text-gray-600">지방</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Instructions */}
                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-purple-500" />
                      조리방법
                    </h3>
                    <div className="space-y-4">
                      {selectedRecipe.instructions?.map((step, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex gap-4 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg hover:shadow-md transition-shadow"
                        >
                          <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <p className="text-gray-700 leading-relaxed">{step}</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Tips */}
                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-yellow-500" />
                      요리 팁
                    </h3>
                    <div className="space-y-3">
                      {selectedRecipe.tips?.map((tip, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200"
                        >
                          <Sparkles className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{tip}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-8 flex flex-col sm:flex-row gap-3">
                    <Button className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white">
                      <Heart className="w-4 h-4 mr-2" />
                      즐겨찾기 추가
                    </Button>
                    <Button variant="outline" className="flex-1 border-gray-300 hover:bg-gray-50">
                      <Bookmark className="w-4 h-4 mr-2" />
                      레시피 저장
                    </Button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}